import React from 'react';
import { Button } from './button';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    totalItems?: number;
    pageSize?: number;
    disabled?: boolean;
    onPageSizeChange?: (size: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
    pageSize,
    disabled = false,
    onPageSizeChange,
}) => {
    if (totalPages <= 1 && !onPageSizeChange) {
        return null;
    }

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 7;

        if (totalPages <= maxPagesToShow) {
            // Показываем все страницы если их мало
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Показываем первую, последнюю и страницы вокруг текущей
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    const handlePrevious = () => {
        if (!disabled && currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (!disabled && currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    return (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
            {onPageSizeChange && (
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
                    Записей на странице:
                    <select
                        aria-label="Записей на странице"
                        className="rounded border p-2 bg-white dark:bg-slate-800 disabled:opacity-50"
                        value={pageSize}
                        disabled={disabled}
                        onChange={event => onPageSizeChange(Number(event.target.value))}
                    >
                        {[10, 20, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                    </select>
                </label>
            )}
            <div className="text-sm text-gray-600 dark:text-slate-300">
                {totalItems !== undefined && pageSize !== undefined && (
                    <>
                        Показано {Math.min((currentPage - 1) * pageSize + 1, totalItems)} -{' '}
                        {Math.min(currentPage * pageSize, totalItems)} из {totalItems}
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    onClick={handlePrevious}
                    disabled={disabled || currentPage === 1}
                    variant="outline"
                    size="sm"
                >
                    ← Назад
                </Button>

                {getPageNumbers().map((page, index) => {
                    if (page === '...') {
                        return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                                ...
                            </span>
                        );
                    }

                    return (
                        <Button
                            key={page}
                            onClick={() => !disabled && onPageChange(page as number)}
                            disabled={disabled}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            className="min-w-[40px]"
                        >
                            {page}
                        </Button>
                    );
                })}

                <Button
                    onClick={handleNext}
                    disabled={disabled || currentPage >= totalPages}
                    variant="outline"
                    size="sm"
                >
                    Вперёд →
                </Button>
            </div>

            <div className="text-sm text-gray-600 dark:text-slate-300">
                Страница {currentPage} из {Math.max(1, totalPages)}
            </div>
        </div>
    );
};

